<section class="units-page">
  <div class="menus-card">
    <div class="menus-card__header">
      <div class="menus-card__title">
        <h1>{{ title() }}</h1>
        <p>{{ description() }}</p>
      </div>

      <div class="header-actions">
        <a class="secondary-link" routerLink="/master/generators">Manage Generators</a>
        <button type="button" class="primary-button" (click)="openCreateForm()">+ Add New</button>
      </div>
    </div>

    <div class="menus-card__filters">
      <label class="search-field search-field--wide">
        <span>Search Unit</span>
        <div class="input-with-icon">
          <span class="input-icon">⌕</span>
          <input
            type="search"
            placeholder="Enter unique no, model, developer, or generator"
            [value]="searchTerm()"
            (input)="onSearch($any($event.target).value)"
          >
        </div>
      </label>
    </div>

    @if (toast()) {
      <div class="toast-message" [class.error]="toast()?.type === 'error'" role="status">
        {{ toast()?.message }}
      </div>
    }

    <div class="table-wrap">
      <table class="table-sm menu-table">
        <thead>
          <tr>
            <th>S.No.</th>
            <th>Unique No.</th>
            <th>Model</th>
            <th>Generator</th>
            <th>Developer</th>
            <th>Purchase Mode</th>
            <th>Capacity (MW)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (unit of paginatedUnits(); track unit.id; let index = $index) {
            <tr>
              <td>{{ showingStart() + index }}</td>
              <td>{{ unit.unique_no }}</td>
              <td>{{ unit.model }}</td>
              <td>{{ generatorLabel(unit.generator_registration_id || null) }}</td>
              <td>{{ unit.name_of_developer }}</td>
              <td>{{ unit.purchase_mode }}</td>
              <td>{{ unit.installed_capacity_mw }}</td>
              <td>
                <div class="action-buttons">
                  <button type="button" class="action-button edit-button" (click)="edit(unit)" aria-label="Edit unit">✎</button>
                  <button type="button" class="action-button delete-button" (click)="remove(unit)" aria-label="Delete unit">🗑</button>
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="8">
                <div class="empty-state">No generation units found.</div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <footer class="table-footer">
      <span>Showing {{ showingStart() }} to {{ showingEnd() }} of {{ totalItems() }} entries</span>

      <div class="pagination">
        <button type="button" class="page-button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">‹</button>
        @for (page of pageNumbers(); track page) {
          <button type="button" class="page-button" [class.active]="page === currentPage()" (click)="goToPage(page)">
            {{ page }}
          </button>
        }
        <button type="button" class="page-button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">›</button>
      </div>
    </footer>
  </div>

  @if (isFormOpen()) {
    <div class="modal-backdrop" role="presentation">
      <section class="menu-modal menu-modal--wide" role="dialog" aria-modal="true" aria-labelledby="unit-modal-title">
        <div class="menu-modal__header">
          <div>
            <p>{{ editingUnitId() ? 'Edit Generating Unit' : 'Add Generating Unit' }}</p>
            <h2 id="unit-modal-title">{{ editingUnitId() ? 'Update unit details' : 'Create a new unit' }}</h2>
          </div>

          <button type="button" class="modal-close" (click)="closeForm()">×</button>
        </div>

        <form class="menu-form" [formGroup]="form" (ngSubmit)="save()">
          <section class="section-card">
            <header class="section-card__header">Basic Information</header>
            <div class="form-grid form-grid--three">
              <label>
                <span>Unique No.</span>
                <input formControlName="unique_no" placeholder="Enter unique number">
              </label>
              <label>
                <span>Model</span>
                <input formControlName="model" placeholder="Enter model">
              </label>
              <label>
                <span>SCADA ID / Turbine ID / GUT No. / Inverter No.</span>
                <input formControlName="scada_turbine_gut_inverter_no" placeholder="Enter SCADA/Turbine/GUT/Inverter No.">
              </label>
              <label>
                <span>Installed Capacity (MW)</span>
                <input formControlName="installed_capacity_mw" placeholder="e.g. 12.345">
              </label>
              <label>
                <span>Latitude</span>
                <input formControlName="latitude" placeholder="e.g. 22.5734">
              </label>
              <label>
                <span>Longitude</span>
                <input formControlName="longitude" placeholder="e.g. 88.3639">
              </label>
              <label>
                <span>Make</span>
                <input formControlName="make" placeholder="Enter make">
              </label>
              <label>
                <span>Name of Developer</span>
                <input formControlName="name_of_developer" placeholder="Enter developer name">
              </label>
              <label class="field--full">
                <span>Identifier Tag</span>
                <input formControlName="identifier_tag" placeholder="Enter identifier tag">
              </label>
            </div>
          </section>

          <section class="section-card">
            <header class="section-card__header">Commercial Information</header>
            <div class="form-grid form-grid--two">
              <label>
                <span>Purchase Mode</span>
                <input formControlName="purchase_mode" placeholder="Purchase by Utility / Consumer">
              </label>
              <label>
                <span>Name of Utility</span>
                <input formControlName="utility_name" placeholder="Enter utility name">
              </label>
              <label>
                <span>Name of Utility in whose area Consumer is connected</span>
                <input formControlName="consumer_utility_name" placeholder="Enter utility name">
              </label>
              <label>
                <span>Name of Consumer</span>
                <input formControlName="consumer_name" placeholder="Enter consumer name">
              </label>
              <label>
                <span>Contracted Capacity (MW)</span>
                <input formControlName="contracted_capacity_mw" placeholder="e.g. 12.345">
              </label>
              <label>
                <span>Contract Rate (₹/kWh)</span>
                <input formControlName="contract_rate_per_kwh" placeholder="e.g. 3.250">
              </label>
              <label>
                <span>Contract From Date</span>
                <input type="date" formControlName="contract_from_date" placeholder="DD/MM/YYYY">
              </label>
              <label>
                <span>Contract To Date</span>
                <input type="date" formControlName="contract_to_date" placeholder="DD/MM/YYYY">
              </label>
              <label>
                <span>Time Block From</span>
                <input formControlName="contract_from_time_block" placeholder="From">
              </label>
              <label>
                <span>Time Block To</span>
                <input formControlName="contract_to_time_block" placeholder="To">
              </label>
            </div>
          </section>

          <section class="section-card">
            <header class="section-card__header">Remarks</header>
            <label>
              <span>Remarks</span>
              <textarea rows="4" formControlName="remarks" placeholder="Enter remarks here..."></textarea>
            </label>
          </section>

          <div class="form-grid form-grid--two">
            <label>
              <span>Linked Generator</span>
              <select formControlName="generator_registration_id">
                <option [ngValue]="null">Unassigned</option>
                @for (generator of utilityRegistrations(); track generator.id) {
                  <option [ngValue]="generator.id">{{ generator.entity_name }}</option>
                }
              </select>
            </label>
          </div>

          <div class="form-actions">
            <button type="button" class="secondary-button" (click)="closeForm()">Cancel</button>
            <button type="submit" class="primary-button" [disabled]="form.invalid">
              {{ editingUnitId() ? 'Update Unit' : 'Create Unit' }}
            </button>
          </div>
        </form>
      </section>
    </div>
  }
</section>
